import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin } from 'antd'
import { cloneDeep, set } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { tryParseJSON } from 'helpers/utils'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import FormButtons from 'elements/FormButtons'
import Checkbox from 'elements/Checkbox'
import Modal from 'elements/Modal'
import SelectDashboardTheme from 'containers/DashboardThemes/Select'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  transformSaveItemParams = ({ params }) => ({
    styleJSON: JSON.stringify(tryParseJSON(params.styleJSON)),
  })

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.dashboardThemeId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || item.systemDefined

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Form.Item label={createLabel('name')}>
              {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
            </Form.Item>
            <Form.Item label={createLabel('description')}>
              {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
            </Form.Item>
            <Form.Item label={createLabel('originDescription')}>
              {createFieldDecorator('originDescription')(<Input disabled />)}
            </Form.Item>
            <Form.Item>
              {createFieldDecorator('active', { valuePropName: 'checked' })(
                <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
              )}
            </Form.Item>
            {process.env.NODE_ENV === 'development' && (
              <div style={{ whiteSpace: 'pre', fontFamily: 'monospace', fontSize: '12px' }}>
                {JSON.stringify(tryParseJSON(item.styleJSON, {}), null, 2)}
              </div>
            )}
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={t('selectDashboardTheme')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedRecord,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedRecord: null,
            })
          }
          width={992}
        >
          <SelectDashboardTheme
            onSelect={(values) => this.setState({ copyFromExistingSelectedRecord: values[0] })}
          />
        </Modal>
      </>
    )
  }
}

export default Form.create()(FormView)
